import React, { useEffect, useReducer, useState } from 'react'
import { string, func, object } from 'prop-types'
import { Router, navigate } from '@reach/router'
import { toast } from 'react-toastify'

import { fetchOutgoing, saveOutgoing } from './actions'
import { initialState, reducer } from './reducer'
import { APP_STATE } from '../../../constants'
import { outgoingShape, userShape } from '../../../types'

import SourceForm from '../SourceForm'
import OutgoingCards from './OutgoingCards'
import Loader from '../../shared/Loader'
import api from '../../../lib/api'
import { cancelable, notCanceled } from '../../../lib/signal'

const Outgoing = ({
  feedId,
  t,
  organisationId,
  organisationName,
  reloadCurrentFeed,
  location,
  selectedOutgoing,
  changeOutgoing,
  user,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [partnerMappings, setPartnerMappings] = useState([])
  const [auditRulesets, setAuditRulesets] = useState([])
  const [jobNotifications, setJobNotifications] = useState([])
  const [feedAuditData, setFeedAuditData] = useState([])
  const [feedAuditDataState, setFeedAuditDataState] = useState(null)
  const { fetchError, items, outgoingListState } = state

  const outgoing = items.map(outgoing => ({
    ...outgoing,
    detailUrl: `/${organisationId}/feeds/${feedId}/outgoing/${outgoing.id}`,
    key: outgoing.id,
  }))

  useEffect(
    () =>
      cancelable(signal => {
        setFeedAuditDataState(APP_STATE.FETCHING)
        fetchOutgoing(feedId, dispatch, signal)

        api
          .get(`/orgs/${organisationId}/partners`, {
            cancelToken: signal && signal.token,
          })
          .then(({ data }) => setPartnerMappings(data))
          .catch(
            notCanceled(err => {
              toast.error(t('outgoing_partner_mapping_error'))
              console.log(err)
            })
          )

        api
          .get(`/orgs/${organisationId}/jobs?feedId=${feedId}&page=1&limit=1`, {
            cancelToken: signal && signal.token,
          })
          .then(({ data }) => {
            const n = data && data[0] && data[0].notifications
            setJobNotifications(n || [])
          })
          .catch(
            notCanceled(err => {
              console.log(err)
            })
          )

        if (user.isAdmin) {
          // Get outgoing audit data
          api
            .get(`/feedaudit/outgoing/page/${feedId}`, {
              cancelToken: signal && signal.token,
            })
            .then(({ data }) => {
              // Add null state to each element of audit data:
              // Null meaning just use feedAuditDataState,
              // otherwise use the individual audit data state (e.g. if reloading individual card)
              data = data.map(a => ({ ...a, State: null }))

              setFeedAuditData(data)
              setFeedAuditDataState(APP_STATE.DONE)
            })
            .catch(
              notCanceled(err => {
                setFeedAuditDataState(APP_STATE.ERROR)
                console.log(err)
              })
            )

          // Get audit ruleset data
          api
            .get(`/feedaudit/rulesets`)
            .then(({ data }) => {
              data = data.map(item => ({
                // Only need names and ids, don't care about the rules contained
                name: item.name,
                id: item.id,
              }))
              setAuditRulesets(data)
            })
            .catch(err => {
              console.log(err)
            })
        }
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(
    () => {
      if (!selectedOutgoing) return
      const newPath = location.href.replace(
        /outgoing\/(.*)/,
        `outgoing/${selectedOutgoing.id}`
      )
      if (location.href !== newPath) navigate(newPath)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOutgoing]
  )

  const publishAllOutgoing = feedId =>
    api
      .post(`/orgs/${organisationId}/jobs`, {
        feedId: feedId,
      })
      .then(() => {
        toast.success(t('outgoing_published_success'))
      })
      .catch(err => {
        toast.error(t('outgoing_published_error'))
        console.log(err)
      })

  const handleFeedAuditDataUpdate = auditData => {
    let updatedFeedAuditData = [...feedAuditData]
    const auditDataIndex = updatedFeedAuditData.findIndex(
      a => a.outgoingId == auditData.outgoingId
    )

    if (auditDataIndex == -1) {
      // outgoing feed is not in list
      updatedFeedAuditData.push(auditData)
    } else {
      updatedFeedAuditData[auditDataIndex] = auditData
    }
    setFeedAuditData(updatedFeedAuditData)
  }

  const reloadIndividualAuditData = outgoingFeedId => {
    if (!user.isAdmin) return
    var index = feedAuditData.findIndex(a => a.outgoingId == outgoingFeedId)

    if (index == -1) {
      // outgoing feed is not in list
      handleFeedAuditDataUpdate({
        outgoingId: outgoingFeedId,
        State: APP_STATE.FETCHING,
      })
    } else {
      handleFeedAuditDataUpdate({
        ...feedAuditData[index],
        State: APP_STATE.FETCHING,
      })
    }

    api
      .get(`/feedaudit/outgoing/${outgoingFeedId}`)
      .then(({ data }) => {
        data.State = null
        handleFeedAuditDataUpdate(data)
      })
      .catch(
        notCanceled(err => {
          handleFeedAuditDataUpdate({
            ...feedAuditData[index],
            State: APP_STATE.ERROR,
          })
          console.log(err)
        })
      )
  }

  const requestAudit = outgoingFeedId => {
    if (!user.isAdmin) return
    var index = feedAuditData.findIndex(a => a.outgoingId == outgoingFeedId)
    if (index == -1) return

    handleFeedAuditDataUpdate({
      ...feedAuditData[index],
      State: APP_STATE.FETCHING,
    })

    api
      .post(`/feedaudit/outgoing/${outgoingFeedId}`)
      .then(() => {
        reloadIndividualAuditData(outgoingFeedId)
      })
      .catch(
        notCanceled(err => {
          handleFeedAuditDataUpdate({
            ...feedAuditData[index],
            State: APP_STATE.ERROR,
          })
          console.log(err)
        })
      )
  }

  const pauseAllOutgoing = feedId => {
    api
      .patch(`/feeds/${feedId}/outgoing/pause-all`)
      .then(() => {
        toast.success(t('outgoing_paused_success'))
      })
      .catch(err => {
        toast.error(t('outgoing_paused_error'))
        console.log(err)
      })
      .then(() => {
        fetchOutgoing(feedId, dispatch)
      })
  }

  const publishSelectedOutgoing = (feedId, outgoingId) => {
    api
      .post(`/orgs/${organisationId}/jobs`, {
        feedId: feedId,
        outgoingIds: [outgoingId],
      })
      .then(() => {
        toast.success(t('outgoing_published_success'))
      })
      .catch(err => {
        toast.error(t('outgoing_published_error'))
        console.log(err)
      })
  }

  const onSave = (connection, format, values, outgoingId) => {
    const { name, enabled, publish, columnMapper, defaultAuditRuleset } = values

    const sourceData = {
      connection,
      format,
      name,
      enabled,
      publish,
      defaultAuditRuleset,
    }
    const partnerId = columnMapper

    saveOutgoing(
      feedId,
      outgoingId,
      sourceData,
      partnerId,
      dispatch,
      t,
      reloadCurrentFeed
    )
  }

  if (outgoingListState === APP_STATE.FETCHING) {
    return <Loader />
  }
  if (outgoingListState === APP_STATE.ERROR) {
    return <p>{fetchError}</p>
  }

  return (
    <Router primary={false}>
      <SourceForm
        feedId={feedId}
        description={t('outgoing_desc')}
        heading={t('outgoing')}
        onCancel={() => navigate('./')}
        onSave={onSave}
        outgoingMapping={partnerMappings}
        organisationId={organisationId}
        organisationName={organisationName}
        location={location}
        change={changeOutgoing}
        path=":resourceId"
        type="outgoing"
        user={user}
        auditRulesets={auditRulesets}
      />
      <OutgoingCards
        key={feedId + '_cardsHolder'}
        feedId={feedId}
        onPublishAll={() => publishAllOutgoing(feedId)}
        onPauseAll={() => pauseAllOutgoing(feedId)}
        onPublishSelected={publishSelectedOutgoing}
        outgoingListState={outgoingListState}
        outgoing={outgoing}
        notifications={jobNotifications}
        organisationId={organisationId}
        templates={partnerMappings}
        path="/"
        feedAuditList={feedAuditData}
        auditDataState={feedAuditDataState}
        reloadIndividualAuditData={reloadIndividualAuditData}
        requestAudit={requestAudit}
        user={user}
        t={t}
      />
    </Router>
  )
}

Outgoing.propTypes = {
  feedId: string.isRequired,
  reloadCurrentFeed: func.isRequired,
  organisationId: string.isRequired,
  organisationName: string.isRequired,
  location: object,
  selectedOutgoing: outgoingShape,
  changeOutgoing: func.isRequired,
  user: userShape.isRequired,
}

export default Outgoing
